import { useEffect, useRef, useState, useCallback } from 'react';
import { useSimStore } from '../../store/useSimStore';
import styles from './MiniMonitor.module.css';

const MIN_W = 280;
const MIN_H = 180;
const DEFAULT_W = 480;
const DEFAULT_H = 300;

// While the user is dragging or resizing, pointer events over the iframe
// get swallowed by the iframe's own document — mousemove never fires on the
// parent window.  We overlay a transparent div to capture them instead.
function useInteractionGuard() {
  const [blocking, setBlocking] = useState(false);
  const start = useCallback(() => setBlocking(true),  []);
  const stop  = useCallback(() => setBlocking(false), []);
  useEffect(() => {
    if (!blocking) return;
    const onUp = () => setBlocking(false);
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
  }, [blocking]);
  return { blocking, start, stop };
}

export default function MiniMonitor() {
  const splitScreen = useSimStore((s) => s.splitScreen);
  const fetalMonitor = useSimStore((s) => s.fetalMonitor);
  const eegOn = useSimStore((s) => s.eegMonitor.enabled);
  const [minimized, setMinimized] = useState(false);
  // Preview-window opacity (1 = solid). Lets the instructor see the controls
  // behind the floating preview. `showOpacity` toggles the slider.
  const [opacity, setOpacity] = useState(1);
  const [showOpacity, setShowOpacity] = useState(false);
  // Position in viewport coordinates (px from top-left).
  // Initialised to bottom-right corner on first render.
  const [pos, setPos] = useState(null);
  const [size, setSize] = useState({ w: DEFAULT_W, h: DEFAULT_H });

  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const dragState = useRef(null);   // { startMouseX, startMouseY, startLeft, startTop }
  const resizeState = useRef(null); // { startMouseX, startMouseY, startW, startH }
  const guard = useInteractionGuard();

  // Compute initial bottom-right position after mount when we know the viewport size
  useEffect(() => {
    const margin = 20;
    setPos({
      x: window.innerWidth  - DEFAULT_W - margin,
      y: window.innerHeight - DEFAULT_H - margin,
    });
  }, []);

  // Re-clamp position when the browser window is resized so the panel never
  // goes off-screen. Also shrink the panel width if it no longer fits.
  useEffect(() => {
    const onResize = () => {
      const el = containerRef.current;
      const w = el ? el.offsetWidth  : DEFAULT_W;
      const h = el ? el.offsetHeight : DEFAULT_H;
      setPos((prev) => {
        if (!prev) return prev;
        return {
          x: Math.max(0, Math.min(window.innerWidth  - w, prev.x)),
          y: Math.max(0, Math.min(window.innerHeight - h, prev.y)),
        };
      });
      setSize((prev) => ({
        w: Math.min(prev.w, window.innerWidth),
        h: Math.min(prev.h, window.innerHeight),
      }));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Keep the window fully on-screen when its height changes (e.g. the vent pane
  // is added on split-screen, or the user resizes). Runs after layout so the
  // real rendered height is known.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const w = el.offsetWidth, h = el.offsetHeight;
    setPos((prev) => {
      if (!prev) return prev;
      const x = Math.max(0, Math.min(window.innerWidth  - w, prev.x));
      const y = Math.max(0, Math.min(window.innerHeight - h, prev.y));
      return (x === prev.x && y === prev.y) ? prev : { x, y };
    });
  }, [splitScreen, size.h, size.w, minimized]);

  // ── Drag (title bar) ────────────────────────────────────────────────────────
  const handleDragMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    guard.start();
    dragState.current = {
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startLeft:   pos.x,
      startTop:    pos.y,
    };
  }, [pos, guard]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragState.current) return;
      const dx = e.clientX - dragState.current.startMouseX;
      const dy = e.clientY - dragState.current.startMouseY;
      const newX = dragState.current.startLeft + dx;
      const newY = dragState.current.startTop  + dy;
      // Clamp so the window stays fully within the viewport
      const el = containerRef.current;
      const w = el ? el.offsetWidth  : DEFAULT_W;
      const h = el ? el.offsetHeight : DEFAULT_H;
      setPos({
        x: Math.max(0, Math.min(window.innerWidth  - w, newX)),
        y: Math.max(0, Math.min(window.innerHeight - h, newY)),
      });
    };
    const onMouseUp = () => { dragState.current = null; };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup',   onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup',   onMouseUp);
    };
  }, []);

  // ── Resize handle (bottom-right corner) ────────────────────────────────────
  const handleResizeMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    guard.start();
    resizeState.current = {
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startW: size.w,
      startH: size.h,
    };
  }, [size, guard]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!resizeState.current) return;
      const dx = e.clientX - resizeState.current.startMouseX;
      const dy = e.clientY - resizeState.current.startMouseY;
      setSize({
        w: Math.max(MIN_W, resizeState.current.startW + dx),
        h: Math.max(MIN_H, resizeState.current.startH + dy),
      });
    };
    const onMouseUp = () => { resizeState.current = null; };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup',   onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup',   onMouseUp);
    };
  }, []);

  if (!pos) return null; // not yet positioned

  // Side-by-side layout: monitor on the left, ventilator + fetal panes to its right.
  const ventW = Math.round(size.w * 0.85);
  const fetalW = Math.round(size.w * 0.9);
  const eegW = Math.round(size.w * 0.62);
  const totalW = size.w
    + (splitScreen ? ventW + 2 : 0)
    + (eegOn ? eegW + 2 : 0)
    + (fetalMonitor ? fetalW + 2 : 0);

  return (
    <div
      ref={containerRef}
      className={styles.floatingWindow}
      style={
        minimized
          ? { right: 20, bottom: 20, top: 'auto', left: 'auto', width: 'auto' }
          : { left: pos.x, top: pos.y, width: totalW }
      }
    >
      {/* Title bar — drag handle */}
      <div
        className={styles.titleBar}
        onMouseDown={handleDragMouseDown}
      >
        <span className={styles.titleDots}>
          <span className={styles.dot} style={{ background: '#FF5F57' }} />
          <span className={styles.dot} style={{ background: '#FEBC2E' }} />
          <span className={styles.dot} style={{ background: '#28C840' }} />
        </span>
        <span className={styles.titleText}>Monitor Preview</span>
        <span className={styles.muteHint} title="Audio plays only on the Monitor window">🔇</span>
        <button
          className={`${styles.iconBtn} ${showOpacity ? styles.iconBtnActive : ''}`}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => setShowOpacity((v) => !v)}
          title="Adjust transparency"
        >
          ◐
        </button>
        <button
          className={styles.minimizeBtn}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => setMinimized((m) => !m)}
          title={minimized ? 'Expand' : 'Minimise'}
        >
          {minimized ? '▲' : '▼'}
        </button>
      </div>

      {/* Transparency slider — adjusts the see-through amount of the preview */}
      {showOpacity && !minimized && (
        <div
          className={styles.opacityBar}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <span className={styles.opacityLabel}>Transparency</span>
          <input
            className={styles.opacitySlider}
            type="range"
            min="0"
            max="80"
            step="5"
            value={Math.round((1 - opacity) * 100)}
            onChange={(e) => setOpacity(1 - Number(e.target.value) / 100)}
          />
          <span className={styles.opacityVal}>{Math.round((1 - opacity) * 100)}%</span>
        </div>
      )}

      {/* Preview iframes — monitor on top, ventilator stacked below when split */}
      {!minimized && (
        <div className={styles.frameWrapper} style={{ opacity }}>
          <div className={styles.pane} style={{ width: size.w, height: size.h }}>
            <iframe
              ref={iframeRef}
              src="/monitor?view=monitor"
              className={styles.frame}
              title="Monitor Preview"
              // The preview (view=monitor) is permanently silenced inside
              // MonitorDisplay — audio plays only on the real Monitor window.
              // No sandbox — needs same-origin BroadcastChannel access
            />
          </div>

          {/* Ventilator preview — to the RIGHT of the monitor when split-screened */}
          {splitScreen && (
            <div className={styles.pane} style={{ width: ventW, height: size.h }}>
              <iframe
                src="/monitor?view=vent"
                className={styles.frame}
                title="Ventilator Preview"
              />
              <span className={styles.paneTag}>VENT</span>
            </div>
          )}

          {/* Depth-of-anaesthesia preview */}
          {eegOn && (
            <div className={styles.pane} style={{ width: eegW, height: size.h }}>
              <iframe src="/monitor?view=eeg" className={styles.frame} title="Depth of Anaesthesia Preview" />
              <span className={styles.paneTag}>EEG</span>
            </div>
          )}

          {/* Fetal CTG preview — to the RIGHT when the fetal monitor is on */}
          {fetalMonitor && (
            <div className={styles.pane} style={{ width: fetalW, height: size.h }}>
              <iframe
                src="/monitor?view=fetal"
                className={styles.frame}
                title="Fetal CTG Preview"
              />
              <span className={styles.paneTag}>CTG</span>
            </div>
          )}

          {/* Transparent overlay: blocks iframes from stealing pointer events
              during drag/resize so mousemove keeps firing on the parent window */}
          {guard.blocking && <div className={styles.iframeGuard} />}
          {/* Resize grip */}
          <div
            className={styles.resizeHandle}
            onMouseDown={handleResizeMouseDown}
            title="Drag to resize"
          />
        </div>
      )}
    </div>
  );
}
